import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import GoogleIcon from "@/components/icons/GoogleIcon";

const GOOGLE_PROFILE_URL = "https://share.google/4rdA2JFo6zTSBbgBa";

const leftLinks = [
  { label: "Home", href: "#hero", id: "hero" },
  { label: "Our Booths", href: "#booths", id: "booths" },
  { label: "Backdrops", href: "#backdrops", id: "backdrops" },
  { label: "Gallery", href: "#gallery", id: "gallery" },
];

const rightLinks = [
  { label: "About Us", href: "#about", id: "about" },
  { label: "Events", href: "#services", id: "services" },
  { label: "Reviews", href: "#reviews", id: "reviews" },
  { label: "FAQ", href: "#faq", id: "faq" },
];

const allLinks = [
  ...leftLinks,
  ...rightLinks,
  { label: "Book Now", href: "#contact", id: "contact" },
];

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("hero");
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const sections = ["hero", "about", "booths", "backdrops", "how-it-works", "services", "gallery", "reviews", "faq"];
      const yt = y + 140;
      let current = "hero";
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= yt) current = id;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const handleAnchor = (e, href) => {
    if (!href.startsWith("#")) return;
    e.preventDefault();
    setOpen(false);
    if (loc.pathname !== "/") {
      nav("/" + href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const NavLink = ({ l }) => (
    <a
      href={l.href}
      onClick={(e) => handleAnchor(e, l.href)}
      className={`nav-link nav-link-dark uppercase tracking-[0.22em] text-[11px] ${active === l.id ? "active" : ""}`}
      data-testid={`nav-link-${l.id}`}
    >
      {l.label}
    </a>
  );

  return (
    <header
      className="fixed top-0 inset-x-0 z-50 transition-all duration-500 bg-ink-900/95 backdrop-blur-xl border-b border-gold-500/15"
      data-testid="site-navigation"
    >
      <nav className="max-w-[1400px] mx-auto px-5 md:px-10 h-24 md:h-28 grid grid-cols-[1fr_auto_1fr] items-center gap-4">
        {/* LEFT LINKS */}
        <ul className="hidden lg:flex items-center justify-end gap-7 xl:gap-10">
          {leftLinks.map((l) => (
            <li key={l.id}><NavLink l={l} /></li>
          ))}
        </ul>

        {/* MOBILE — left spacer */}
        <div className="lg:hidden" />

        {/* CENTER LOGO */}
        <Link
          to="/"
          className="flex items-center justify-center group justify-self-center"
          data-testid="nav-logo-link"
          aria-label="Captured Moments Photo Booth — Home"
        >
          <img
            src="/assets/logo/logo-gold.png"
            alt="Captured Moments Photo Booth"
            className="h-16 md:h-20 w-auto transition-transform duration-500 group-hover:scale-105"
          />
        </Link>

        {/* RIGHT LINKS + BOOK NOW */}
        <ul className="hidden lg:flex items-center justify-start gap-7 xl:gap-10">
          {rightLinks.map((l) => (
            <li key={l.id}><NavLink l={l} /></li>
          ))}
          <li>
            <a
              href={GOOGLE_PROFILE_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="View our Google profile"
              data-testid="nav-google-icon"
              className="group inline-flex items-center justify-center w-10 h-10 rounded-full border border-cream-50/25 hover:border-gold-400 hover:bg-white/[0.04] transition-all duration-300"
            >
              <GoogleIcon size={16} className="transition-transform duration-300 group-hover:scale-110" />
            </a>
          </li>
          <li>
            <a
              href="#contact"
              onClick={(e) => handleAnchor(e, "#contact")}
              className="group inline-flex items-center gap-3 px-5 md:px-6 py-3 border border-cream-50/60 text-cream-50 uppercase tracking-[0.22em] text-[11px] hover:border-gold-400 hover:text-gold-300 hover:bg-white/[0.04] transition-all duration-300"
              data-testid="nav-book-now-cta"
            >
              <span>Book Now</span>
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </li>
        </ul>

        {/* MOBILE TOGGLE */}
        <button
          className="lg:hidden p-2 text-white justify-self-end"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden bg-ink-900/97 backdrop-blur-xl border-t border-gold-500/15"
            data-testid="mobile-menu"
          >
            <ul className="px-6 py-6 space-y-3">
              {allLinks.map((l, i) => (
                <motion.li
                  key={l.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <a
                    href={l.href}
                    onClick={(e) => handleAnchor(e, l.href)}
                    className="block py-3 text-base uppercase tracking-[0.22em] text-cream-50 hover:text-gold-300"
                    data-testid={`mnav-link-${l.id}`}
                  >
                    {l.label}
                  </a>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
